import { useMutation } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import type { TraceStructuredFilterGroupRequest } from "../traces/useTracesSearch";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

export interface StartSmartTableTraceImportResponse {
  success: boolean;
  operation_id: string;
  message: string;
}

interface SmartTableTraceImportBasePayload {
  operation_id?: string;
  limit?: number;
  anchor_column_id?: string;
}

export interface SmartTableTraceImportByIdsPayload extends SmartTableTraceImportBasePayload {
  trace_ids: string[];
}

export interface SmartTableTraceImportByFilterPayload extends SmartTableTraceImportBasePayload {
  all_matching: true;
  filter_group?: TraceStructuredFilterGroupRequest | null;
  sort_field?: string;
  sort_order?: "asc" | "desc";
}

export type SmartTableTraceImportPayload =
  | SmartTableTraceImportByIdsPayload
  | SmartTableTraceImportByFilterPayload;

interface StartSmartTableTraceImportArgs {
  tableId: string;
  sheetId: string;
  payload: SmartTableTraceImportPayload;
}

export const useStartSmartTableTraceImport = () => {
  const userToken = useAuth()?.userToken ?? "";
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const wid = Number(workspaceId);

  return useMutation<StartSmartTableTraceImportResponse, unknown, StartSmartTableTraceImportArgs>({
    mutationFn: async ({ tableId, sheetId, payload }) => {
      const response = await fetch(ENDPOINTS.smart_table_trace_imports(wid, tableId, sheetId), {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(payload),
      });

      return (await handleResponse(response)) as StartSmartTableTraceImportResponse;
    },
  });
};
